import { useCallback, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTERS_PATH } from '../../routers/routers';
import { CaptureService } from '../../services/capture-exercise/capture-exercise.service';
import { showInfoToast } from '../../components/Toaster';
import { isMobileDevice } from '../../utils/utils';
import { HomeworkStep, HomeworkMessage } from './useExercisesHomework';
import { TrackingService } from '../../services/tracking/tracking.service';
import { useExerciseStore } from '../../utils/exerciseStore';

interface ImageHandlersProps {
  setMessages: React.Dispatch<React.SetStateAction<HomeworkMessage[]>>;
  addBotMessage: (msg: Partial<HomeworkMessage>, delay?: number) => Promise<string>;
  setLocalExerciseData: React.Dispatch<React.SetStateAction<any>>;
  setLocalCapturedImageUrl: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<HomeworkStep>>;
  setIsPolling: React.Dispatch<React.SetStateAction<boolean>>;
  pollingTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  isMountedRef: React.MutableRefObject<boolean>;
  genderSuffix: string;
}

const trackingService = new TrackingService();

export function useExercisesHomeworkImageHandlers({
  setMessages,
  addBotMessage,
  setLocalExerciseData,
  setLocalCapturedImageUrl,
  setCurrentStep,
  setIsPolling,
  pollingTimeoutRef,
  isMountedRef,
  genderSuffix,
}: ImageHandlersProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isPollingRef = useRef<boolean>(false);
  const processingStepIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isMobileActive, setIsMobileActive] = useState<boolean>(false);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      isPollingRef.current = false;
      if (processingStepIntervalRef.current) {
        clearInterval(processingStepIntervalRef.current);
        processingStepIntervalRef.current = null;
      }
    };
  }, []);

  const startManualCapture = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (isMobileDevice()) {
      input.setAttribute('capture', 'environment');
    }
    input.onchange = async (event: any) => {
      const files = Array.from(event.target.files || []) as File[];
      if (files.length > 0) {
        const file = files[0];
        if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
          showInfoToast(t('onlyImagesAllowed') || 'Please select an image file only (JPEG or PNG)');
          return;
        }

        try {
          const objectUrl = URL.createObjectURL(file);

          isPollingRef.current = false;
      if (processingStepIntervalRef.current) {
        clearInterval(processingStepIntervalRef.current);
        processingStepIntervalRef.current = null;
      }
          setIsPolling(false);
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          setMessages(prev => prev.filter(m => m.id !== 'qr-code-bubble'));

          navigate(ROUTERS_PATH.CAPTUREEXERCISE, {
            state: {
              capturedImageUrl: objectUrl,
              fromHomework: true,
              isFileUpload: true
            }
          });
        } catch (err) {
          console.error('Failed to process image file:', err);
        }
      }
    };
    input.click();
  }, [navigate, pollingTimeoutRef, setIsPolling, setMessages, t]);

  const goToCapture = useCallback(async () => {
    trackingService.trackEvent('homework_capture_clicked', { source: 'exercises_homework' });
    
    if (isMobileDevice()) {
      startManualCapture();
      return;
    }

    // Desktop QR Flow
    setIsPolling(false);
    isPollingRef.current = false;
    setIsMobileActive(false);

    const sessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const qrBubbleId = 'qr-code-bubble';

    // Add loading QR code bubble message immediately
    setMessages(prev => [
      ...prev.filter(m => m.id !== qrBubbleId),
      {
        id: qrBubbleId,
        type: 'qr_code',
        text: '',
        qrCodeUrl: undefined,
      }
    ]);

    try {
      const captureService = new CaptureService();
      const response = await captureService.GENERATETEMPTOKEN(sessionId);
      const { token } = response;
      const qrCodeUrl = `${window.location.origin}/capture-exercise?sessionId=${sessionId}&token=${token}`;

      // Update QR code bubble message with the tokenized URL
      setMessages(prev =>
        prev.map(m =>
          m.id === qrBubbleId
            ? { ...m, text: qrCodeUrl, qrCodeUrl }
            : m
        )
      );

      // Start Polling
      setIsPolling(true);
      isPollingRef.current = true;
      
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 5;
      let hasShownProcessingPreview = false;
      const loaderId = 'hw-analyzing-loader';
      const previewBubbleId = `qr-captured-image-${sessionId}`;
      const clearProcessingInterval = () => {
        if (processingStepIntervalRef.current) {
          clearInterval(processingStepIntervalRef.current);
          processingStepIntervalRef.current = null;
        }
      };
      
      const poll = async () => {
        if (!isMountedRef.current || !isPollingRef.current) return;
        try {
          const res = await captureService.GETVALIDATEEXECISE(sessionId);
          consecutiveErrors = 0; // Reset consecutive error count on any response from server
          
          if (res?.is_active) {
            setIsMobileActive(true);
          }

          if (res?.status === "processing" && res.capture_image_b64) {
            if (!isMountedRef.current || !isPollingRef.current) return;
            if (!hasShownProcessingPreview) {
              hasShownProcessingPreview = true;
              const loadingSteps = [
                t('hwAnalyzingStep1'),
                t('hwAnalyzingStep2'),
                t('hwAnalyzingStep3'),
              ];
              let stepIdx = 0;
              setMessages(prev => [
                ...prev.filter(m => m.id !== qrBubbleId && m.id !== previewBubbleId && m.id !== loaderId),
                {
                  id: previewBubbleId,
                  sender: 'bot',
                  text: '',
                  type: 'captured_image',
                  imageUrl: res.capture_image_b64,
                },
                {
                  id: loaderId,
                  sender: 'bot',
                  text: '',
                  type: 'loader_bubble',
                  loadingText: loadingSteps[0],
                },
              ]);
              clearProcessingInterval();
              processingStepIntervalRef.current = setInterval(() => {
                if (!isMountedRef.current || !isPollingRef.current) return;
                stepIdx = Math.min(stepIdx + 1, loadingSteps.length - 1);
                setMessages(prev =>
                  prev.map(m =>
                    m.id === loaderId
                      ? { ...m, loadingText: loadingSteps[stepIdx] }
                      : m
                  )
                );
              }, 2500);
            }
          }

          if (res?.status === "invalid_image" || res?.is_math_exercise === false) {
            if (!isMountedRef.current || !isPollingRef.current) return;
            clearProcessingInterval();
            setMessages(prev => prev.filter(m => m.id !== qrBubbleId && m.id !== loaderId && m.id !== previewBubbleId));
            setIsPolling(false);
            isPollingRef.current = false;
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }
            setLocalExerciseData(res);
            setLocalCapturedImageUrl(res.capture_image_b64 || null);
            setCurrentStep(HomeworkStep.IMAGE_RECEIVED);
            return;
          }

          if (res?.status === "success" && (res.exercise_content || res.capture_image_b64)) {
            if (!isMountedRef.current || !isPollingRef.current) return;
            
            // Remove QR code bubble and analyzing loader
            clearProcessingInterval();
            setMessages(prev => prev.filter(m => m.id !== qrBubbleId && m.id !== loaderId));
            useExerciseStore.getState().setCaptureImageSessionId(sessionId);
            
            // Stop polling
            setIsPolling(false);
            isPollingRef.current = false;
      if (processingStepIntervalRef.current) {
        clearInterval(processingStepIntervalRef.current);
        processingStepIntervalRef.current = null;
      }
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }

            if (res.exercise_id) {
              navigate(ROUTERS_PATH.DISPLAYEXERCISE, {
                state: { 
                  exerciseData: res,
                  from: "exercises-homework"
                },
              });
            } else {
              setMessages(prev => prev.filter(m => m.id !== previewBubbleId));
              setLocalExerciseData(res);
              setLocalCapturedImageUrl(res.capture_image_b64);
              setCurrentStep(HomeworkStep.IMAGE_RECEIVED);
            }
            return;
          }
        } catch (error: any) {
          consecutiveErrors++;
          console.error(`Polling error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}) on homework page:`, error.response?.data || error.message);
          
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.warn("Max consecutive polling errors reached. Stopping polling.");
            setIsPolling(false);
            isPollingRef.current = false;
      if (processingStepIntervalRef.current) {
        clearInterval(processingStepIntervalRef.current);
        processingStepIntervalRef.current = null;
      }
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }
            
            // Add error bubble
            addBotMessage({
              id: `qr-timeout-error-${Date.now()}`,
              text: t('generalError'),
            });
            return;
          }
        }
        
        if (isMountedRef.current && isPollingRef.current) {
          pollingTimeoutRef.current = setTimeout(poll, 1500);
        }
      };

      poll();

      // Stop polling after 5 minutes
      setTimeout(() => {
        if (isMountedRef.current && isPollingRef.current) {
          setIsPolling(false);
          isPollingRef.current = false;
      if (processingStepIntervalRef.current) {
        clearInterval(processingStepIntervalRef.current);
        processingStepIntervalRef.current = null;
      }
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          setMessages(prev => prev.filter(m => m.id !== qrBubbleId));
          addBotMessage({
            id: `qr-timeout-${Date.now()}`,
            text: t('solutionCaptureTimeout' + genderSuffix),
          });
        }
      }, 5 * 60 * 1000);

    } catch (error) {
      console.error("Failed to generate QR code on homework page:", error);
      setIsPolling(false);
      isPollingRef.current = false;
      if (processingStepIntervalRef.current) {
        clearInterval(processingStepIntervalRef.current);
        processingStepIntervalRef.current = null;
      }
      await addBotMessage({
        id: `qr-error-${Date.now()}`,
        text: t('qrCodeGenerationFailed') || 'QR code generation failed. Please try again.',
        type: 'text',
      });
    }
  }, [navigate, addBotMessage, t, setMessages, setIsPolling, setLocalExerciseData, setLocalCapturedImageUrl, setCurrentStep, isMountedRef, pollingTimeoutRef, startManualCapture, genderSuffix]);

  return {
    goToCapture,
    startManualCapture,
    isMobileActive
  };
}
